import {
  Box,
  Heading,
  VStack,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";

import { OfferDetails } from "../../Platform/Common/types";
import {
  getThemeColors,
  getOfferTierStyles,
  offerMarkdownComponents,
} from "./OfferCardShared";

interface OfferCardProps {
  offer: OfferDetails;
  domainSlug: string;
}

const OfferCard: React.FC<OfferCardProps> = ({
  offer,
  domainSlug,
}) => {
  const navigate = useNavigate();

  const colors = getThemeColors(offer.theme_key);
  const badgeColors = getThemeColors(offer.badge_theme);

  const tierStyles = getOfferTierStyles(
    offer.promotion_tier,
    colors
  );

  const isHero = offer.promotion_tier === "HERO";

  const cardBg = useColorModeValue(
    tierStyles.cardBgLight,
    tierStyles.cardBgDark
  );

  const borderColor = useColorModeValue(
    tierStyles.borderColorLight,
    tierStyles.borderColorDark
  );

  const topBarBg = useColorModeValue(
    tierStyles.topBarBgLight,
    tierStyles.topBarBgDark
  );

  const badgeBg = useColorModeValue(
    badgeColors.badgeBgLight,
    badgeColors.badgeBgDark
  );

  const badgeText = useColorModeValue(
    badgeColors.badgeTextLight,
    badgeColors.badgeTextDark
  );

  const durationBg = useColorModeValue(
    "gray.100",
    "gray.700"
  );

  const descColor = useColorModeValue(
    "gray.600",
    "gray.300"
  );

  const currencySymbol =
    process.env.REACT_APP_PLATFORM_CURRENCY_SYMBOL || "$";

  return (
    <Box
      bg={cardBg}
      borderRadius="xl"
      borderWidth={tierStyles.borderWidth}
      borderColor={borderColor}
      boxShadow={tierStyles.boxShadow}
      position="relative"
      overflow="visible"
      transition="all 0.25s ease"
      h="100%"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      _hover={{
        transform: tierStyles.hoverTransform,
        boxShadow: tierStyles.hoverShadow,
      }}
    >
      {/* Tier accent bar
          Hero is intentionally stronger.
          Standard and Featured use the same height. */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        h={isHero ? "7px" : "5px"}
        bg={topBarBg}
        borderTopLeftRadius="xl"
        borderTopRightRadius="xl"
        zIndex={0}
      />

      {offer.badge_text && (
        <Box
          position="absolute"
          top={isHero ? "-16px" : "-13px"}
          left={isHero ? "22px" : "20px"}
          bg={badgeBg}
          color={badgeText}
          px={isHero ? 4 : 3}
          py={isHero ? 1.5 : 1}
          fontSize={isHero ? "sm" : "xs"}
          borderRadius="full"
          fontWeight="700"
          letterSpacing="0.01em"
          boxShadow="sm"
          zIndex={2}
        >
          {offer.badge_text}
        </Box>
      )}

      <VStack
        align="start"
        spacing={5}
        flex="1"
        p={tierStyles.padding}
        pt={8}
      >
        {/* Same typography across all tiers */}
        <Heading
          size={tierStyles.titleSize}
          fontWeight={tierStyles.titleWeight}
          fontFamily="heading"
          letterSpacing="-0.025em"
          lineHeight="1.2"
        >
          {offer.name}
        </Heading>

        {offer.duration_label && (
          <Box
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
            bg={durationBg}
            fontWeight="600"
          >
            {offer.duration_label}
          </Box>
        )}

        {offer.description && (
          <Box
            fontSize="md"
            color={descColor}
            lineHeight="1.65"
            width="100%"
          >
            <ReactMarkdown components={offerMarkdownComponents}>
              {offer.description}
            </ReactMarkdown>
          </Box>
        )}
      </VStack>

      <Box
        px={tierStyles.padding}
        pb={tierStyles.padding}
        pt={2}
      >
        {/* Same price typography across all tiers */}
        <Heading
          size={tierStyles.priceSize}
          fontWeight={tierStyles.priceWeight}
          mb={4}
          fontFamily="heading"
          letterSpacing="-0.03em"
        >
          {currencySymbol} {offer.total_price}
        </Heading>

        <Button
          w="100%"
          size={tierStyles.buttonSize}
          fontWeight="700"
          colorScheme={offer.theme_key?.toLowerCase()}
          onClick={() =>
            navigate(
              `/practice/${domainSlug}/${offer.slug}`
            )
          }
        >
          View Details
        </Button>
      </Box>
    </Box>
  );
};

export default OfferCard;